import { useEffect, useState } from "react";
import { api, apiError } from "../lib/api";
import { AlertTriangle, Plus, X } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";
import { canEdit } from "../lib/permissions";

const SEV_RANK = { "Life-Threatening": 0, Severe: 1, Moderate: 2, Mild: 3, Minimal: 4 };
const SEV_OPTIONS = ["Mild", "Moderate", "Severe", "Life-Threatening", "Minimal"];
const TYPE_OPTIONS = ["Drug", "Food", "Environmental", "Biologic", "Other"];
const isHotSev = (s) => s === "Severe" || s === "Life-Threatening";
const EMPTY = { allergy: "", type: "Drug", severity: "Moderate", reaction: "" };

function QuickAddForm({ clientId, onDone, onCancel }) {
  const [form, setForm] = useState(EMPTY);
  const [busy, setBusy] = useState(false);
  const f = (k) => (e) => setForm((s) => ({ ...s, [k]: e.target.value }));
  const submit = async (e) => {
    e.preventDefault();
    if (!form.allergy.trim()) return toast.error("Allergy name is required");
    setBusy(true);
    try {
      await api.post(`/clients/${clientId}/allergies`, form);
      toast.success("Allergy added to the chart");
      onDone();
    } catch (err) { toast.error(apiError(err)); }
    setBusy(false);
  };
  return (
    <form onSubmit={submit} data-testid="allergy-quickadd-form"
      className="absolute right-0 top-full z-40 mt-2 w-[320px] rounded-2xl border border-[#EFEBE0] bg-white p-4 shadow-2xl">
      <div className="mb-2 flex items-center justify-between">
        <div className="font-heading text-sm font-bold text-[#3E2723]">Quick Add Allergy</div>
        <button type="button" onClick={onCancel} data-testid="allergy-quickadd-cancel"
          className="rounded-lg p-1 text-[#7B6157] hover:bg-[#F5F2EB]"><X size={14} /></button>
      </div>
      <div className="space-y-2 text-xs">
        <div>
          <label className="mb-0.5 block font-semibold text-[#5D4037]">Allergen</label>
          <input data-testid="allergy-quickadd-name" required autoFocus value={form.allergy} onChange={f("allergy")}
            placeholder="e.g., Penicillin"
            className="w-full rounded-lg border border-[#E5DFD3] px-2.5 py-1.5 text-[13px] outline-none focus:border-[#8D6E63]" />
        </div>
        <div className="grid grid-cols-2 gap-2">
          <div>
            <label className="mb-0.5 block font-semibold text-[#5D4037]">Type</label>
            <select data-testid="allergy-quickadd-type" value={form.type} onChange={f("type")}
              className="w-full rounded-lg border border-[#E5DFD3] px-2.5 py-1.5 text-[13px] outline-none focus:border-[#8D6E63]">
              {TYPE_OPTIONS.map((t) => <option key={t}>{t}</option>)}
            </select>
          </div>
          <div>
            <label className="mb-0.5 block font-semibold text-[#5D4037]">Severity</label>
            <select data-testid="allergy-quickadd-severity" value={form.severity} onChange={f("severity")}
              className="w-full rounded-lg border border-[#E5DFD3] px-2.5 py-1.5 text-[13px] outline-none focus:border-[#8D6E63]">
              {SEV_OPTIONS.map((t) => <option key={t}>{t}</option>)}
            </select>
          </div>
        </div>
        <div>
          <label className="mb-0.5 block font-semibold text-[#5D4037]">Reaction (optional)</label>
          <input data-testid="allergy-quickadd-reaction" value={form.reaction} onChange={f("reaction")}
            placeholder="e.g., Hives, throat swelling"
            className="w-full rounded-lg border border-[#E5DFD3] px-2.5 py-1.5 text-[13px] outline-none focus:border-[#8D6E63]" />
        </div>
      </div>
      <div className="mt-3 flex justify-end gap-2">
        <button type="submit" disabled={busy} data-testid="allergy-quickadd-save"
          className="rounded-full bg-[#4E342E] px-3.5 py-1.5 text-xs font-bold text-white hover:bg-[#3E2723] disabled:opacity-60">
          {busy ? "Saving…" : "Add to Chart"}
        </button>
      </div>
    </form>
  );
}

export function AllergyBanner({ clientId }) {
  const { user } = useAuth();
  const [rows, setRows] = useState(null);
  const [showAdd, setShowAdd] = useState(false);
  const load = () => {
    if (!clientId) { setRows([]); return; }
    api.get(`/clients/${clientId}/allergies`).then((r) => setRows(r.data)).catch(() => setRows([]));
  };
  useEffect(() => { setRows(null); load(); }, [clientId]); // eslint-disable-line
  const editable = canEdit(user, "clinical");
  const isEmpty = !rows || rows.length === 0;
  if (isEmpty && !editable) return null;
  const sorted = rows ? [...rows].sort((a, b) => (SEV_RANK[a.severity] ?? 5) - (SEV_RANK[b.severity] ?? 5)) : [];
  const severeCount = sorted.filter((a) => isHotSev(a.severity)).length;
  const hot = severeCount > 0;
  return (
    <div className="relative">
      <div data-testid="allergy-banner"
        className={`mb-3 flex flex-wrap items-center gap-2 rounded-xl border px-4 py-2.5 ${
          isEmpty ? "border-[#EFEBE0] bg-[#FDFCF8]"
          : hot ? "border-[#B71C1C] bg-[#C62828]" : "border-[#FFCDD2] bg-[#FFEBEE]"}`}>
        <AlertTriangle size={15} className={isEmpty ? "text-[#7B6157]" : hot ? "text-white" : "text-[#C62828]"} />
        <span className={`text-xs font-black uppercase tracking-wide ${
          isEmpty ? "text-[#7B6157]" : hot ? "text-white" : "text-[#C62828]"}`}>
          {isEmpty ? "No known allergies on file" : `Allergies${hot ? ` · ${severeCount} severe` : ""}`}
        </span>
        {sorted.map((a) => (
          <span key={a.id} data-testid={`allergy-chip-${a.id}`} title={a.reaction ? `Reaction: ${a.reaction}` : undefined}
            className={`rounded-full px-2.5 py-1 text-[11px] font-bold ${
              isHotSev(a.severity) ? "bg-white text-[#B71C1C]"
              : hot ? "bg-white/20 text-white"
              : "border border-[#FFCDD2] bg-white text-[#C62828]"}`}>
            {a.allergy || a.allergen}{a.severity ? ` · ${a.severity}` : ""}
          </span>
        ))}
        {editable && (
          <button data-testid="allergy-quickadd-btn" onClick={() => setShowAdd((v) => !v)}
            className={`ml-auto inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-[11px] font-bold transition-colors ${
              isEmpty ? "border border-[#E5DFD3] bg-white text-[#4E342E] hover:bg-[#F5F2EB]"
              : hot ? "bg-white/20 text-white hover:bg-white/30"
              : "border border-[#FFCDD2] bg-white text-[#C62828] hover:bg-[#FFEBEE]"}`}>
            <Plus size={12} /> Add allergy
          </button>
        )}
      </div>
      {showAdd && (
        <QuickAddForm clientId={clientId} onCancel={() => setShowAdd(false)}
          onDone={() => { setShowAdd(false); load(); }} />
      )}
    </div>
  );
}
